import React, { useState, useEffect } from 'react';
import Toggle from 'react-toggle';
import { useTranslations } from '../../localization/useTranslations';
import Icon from '../../common/components/Icon';
import { usePremiumStatus } from '../helpers/CheckPremiumStatus';
import { SimpleNewsletterCardProps } from '../types/NewsletterPage';
import { getNewsletterDetail } from '../../utils/getNewsletterFields';
import useScrollbackAfterRedirect from '../../hooks/useScrollbackAfterRedirect';
import PremiumNewsletterModal from '../../newsletter-landing/components/PremiumNewsletterModal';
import { checkIfAlreadySubscribedToPremium } from '../../newsletter-landing/helpers/CheckIfAlreadySubscribedToPremium';
import { handleNewsletterToggle } from '../../newsletter-landing/helpers/handleNewsletterToggle'; // Import the helper

const SimpleNewsletterCard: React.FC<SimpleNewsletterCardProps> = ({ newsletter, isSubscribed, onChange }) => {
  useScrollbackAfterRedirect(); // Restore scroll position if redirected from login

  // Extract isUserPremium and isPremiumStateLoading from the hook
  const { isUserPremium } = usePremiumStatus();

  const [isAlreadySubscribedToPremium, setIsAlreadySubscribedToPremium] = useState<boolean>(false);
  const [isToggleChecked, setIsToggleChecked] = useState<boolean>(isSubscribed);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const translations = useTranslations();

  const newsletterPeriod = getNewsletterDetail(newsletter, 'period');
  const newsletterTitle = getNewsletterDetail(newsletter, 'title');

  const modalContent = {
    ...window.premiumModalFields,
    header_background_image:
      typeof window.premiumModalFields?.header_background_image === 'string'
        ? window.premiumModalFields?.header_background_image
        : undefined,
  };

  // Check if the user is already subscribed to the premium newsletter
  useEffect(() => {
    const isSubscribedToPremium = checkIfAlreadySubscribedToPremium(newsletter.code);
    setIsAlreadySubscribedToPremium(isSubscribedToPremium);
  }, [newsletter.code]);

  // Sync toggle state with prop
  useEffect(() => {
    setIsToggleChecked(isSubscribed);
  }, [isSubscribed]);

  // Use the helper function to handle the toggle change
  const handleToggleChange = () => {
    const premiumStatus = isUserPremium ?? false;

    handleNewsletterToggle(
      newsletter,
      premiumStatus,
      isAlreadySubscribedToPremium,
      setIsDialogOpen,
      setIsToggleChecked,
      onChange,
      setIsAlreadySubscribedToPremium
    );
  };

  function getFollowButtonLabel() {
    return isToggleChecked
      ? translations?.authorAndTopic?.unfollow || 'Unfollow'
      : translations?.authorAndTopic?.follow || 'Follow';
  }

  return (
    <div className="newsletter-simple-card">
      {/* Mobile Layout */}
      <div className="newsletter-simple-card--mobile-layout">
        <div className="newsletter-simple-card__header">
          <div className="newsletter-simple-card__image-wrapper">
            <img src={newsletter.image_url} alt={newsletter.title} className="newsletter-simple-card__image" />
          </div>
          <div>
            {newsletterPeriod && <span className="newsletter-simple-card__period basic-14">{newsletterPeriod}</span>}
            <h5 className="newsletter-simple-card__title">{newsletterTitle}</h5>
          </div>
        </div>
        <div className="newsletter-simple-card__content">
          <p className="newsletter-simple-card__description basic-14">{newsletter.description}</p>

          <div className="newsletter-simple-card__bottom">
            <div className="newsletter-simple-card__subscribe-container">
              <label className="link-16">
                <Toggle
                  checked={isToggleChecked}
                  icons={false}
                  onChange={handleToggleChange}
                  aria-label={getFollowButtonLabel()}
                />
                {isToggleChecked ? (
                  <span>{translations?.authorAndTopic?.unfollow || 'Unfollow'}</span>
                ) : (
                  <span>{translations?.authorAndTopic?.follow || 'Follow'}</span>
                )}
              </label>
            </div>

            {newsletter.preview_url && (
              <a
                href={newsletter.preview_url}
                className="newsletter-simple-card__link link-16"
                target="_blank"
                rel="noreferrer"
              >
                {translations?.newsletter?.previewLabel || 'View'}
                <Icon name="external-link" size="small" />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="newsletter-simple-card--desktop-layout">
        <div className="newsletter-simple-card__image-wrapper">
          <img src={newsletter.image_url} alt={newsletter.title} className="newsletter-simple-card__image" />
        </div>

        <div className="newsletter-simple-card__content">
          <div>
            {newsletterPeriod && <span className="newsletter-simple-card__period basic-14">{newsletterPeriod}</span>}
            <h5 className="newsletter-simple-card__title">{newsletterTitle}</h5>
            <p className="newsletter-simple-card__description basic-14">{newsletter.description}</p>
          </div>

          <div className="newsletter-simple-card__bottom">
            <div className="newsletter-simple-card__subscribe-container">
              <label className="link-16">
                <Toggle
                  checked={isToggleChecked}
                  icons={false}
                  onChange={handleToggleChange}
                  aria-label={getFollowButtonLabel()}
                />
                {isToggleChecked ? (
                  <span>{translations?.authorAndTopic?.unfollow || 'Unfollow'}</span>
                ) : (
                  <span>{translations?.authorAndTopic?.follow || 'Follow'}</span>
                )}
              </label>
            </div>

            {newsletter.preview_url && (
              <a
                href={newsletter.preview_url}
                className="newsletter-simple-card__link link-16"
                target="_blank"
                rel="noreferrer"
              >
                {translations?.newsletter?.previewLabel || 'View'}
                <Icon name="external-link" size="small" />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Premium Newsletter Modal */}
      {newsletter.is_premium && (
        <PremiumNewsletterModal
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          modalContent={modalContent}
          // shouldOpenPremodal={shouldOpenPremodal}
        />
      )}
    </div>
  );
};

export default SimpleNewsletterCard;
